import React, { useEffect, useMemo, useRef, useState } from "react";
import Sortable from "sortablejs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message, Modal, Input, Spin, Tag } from "antd";
import { X, Plus, Edit2, Trash2 } from "react-feather";

import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";

import ClipPath from "../../../../assets/icons/ic_clip-path.svg";
import "./stylesEventMarkets.css";

const ENTITY_TYPES = {
  PREMATCH: "PREMATCH",
  LIVE: "LIVE",
};

function EventMarkets() {
  const token = JSON.parse(localStorage.getItem("token"));
  const api = useMemo(() => createPrivateAdminPanelInstance(token?.IdToken), [token?.IdToken]);
  const qc = useQueryClient();

  const [entityType, setEntityType] = useState(ENTITY_TYPES.PREMATCH);
  const [activeSportId, setActiveSportId] = useState(null);
  const [selectedCardsTabId, setSelectedCardsTabId] = useState(null); // selected tab for Markets tables

  const [showAddTabModal, setShowAddTabModal] = useState(false);
  const [newTabName, setNewTabName] = useState("");

  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [targetTabForAddCard, setTargetTabForAddCard] = useState(null); // tabId
  const [addMarketQuery, setAddMarketQuery] = useState("");
  const [addMarketSelected, setAddMarketSelected] = useState(null);
  const [debouncedMarketQuery, setDebouncedMarketQuery] = useState("");

  const [showRenameMarketModal, setShowRenameMarketModal] = useState(false);
  const [renameMarketPayload, setRenameMarketPayload] = useState(null); // {sport_id, market_id, current_display_name}
  const [newMarketDisplayName, setNewMarketDisplayName] = useState("");

  const [deleteTabId, setDeleteTabId] = useState(null); // tabId or null
  const [deleteCardTarget, setDeleteCardTarget] = useState(null); // { tabId, marketId } or null

  const entityTypeLabel = entityType === ENTITY_TYPES.LIVE ? "live" : "pre match";

  // Sports list (top row)
  const sportsQuery = useQuery({
    queryKey: ["event-markets", "sports", entityType],
    queryFn: async () => {
      const res = await api.post(API_ROUTES.GET_EVENT_MARKETS_SPORTS, {
        entity_type: entityType,
      });
      const sports = res?.data?.data?.sports || [];
      return sports.map((s) => ({ id: s.sport_id, name: s.name }));
    },
    refetchOnWindowFocus: false,
  });

  const activeSportName = useMemo(() => {
    const sport = (sportsQuery.data || []).find((s) => s.id === activeSportId);
    return sport?.name || "sport";
  }, [sportsQuery.data, activeSportId]);

  // set default active sport
  useEffect(() => {
    if (!sportsQuery.isLoading && sportsQuery.data && sportsQuery.data.length) {
      if (!activeSportId || !sportsQuery.data.find((s) => s.id === activeSportId)) {
        setActiveSportId(sportsQuery.data[0].id);
      }
    }
  }, [sportsQuery.isLoading, sportsQuery.data, activeSportId]);

  // Tabs for active sport
  const tabsQuery = useQuery({
    queryKey: [
      "event-markets",
      "tabs",
      entityType,
      activeSportId || "none",
    ],
    enabled: !!activeSportId,
    queryFn: async () => {
      const res = await api.post(API_ROUTES.GET_MARKET_ENTITIES_BY_TYPE, {
        entity_type: entityType,
        sport_id: activeSportId,
        item_type: "TAB",
        parent_type: "SPORT",
        parent_id: activeSportId,
      });
      const items = res?.data?.data?.entities_data || [];
      return items.map((t, idx) => ({
        entity_id: t.entity_id,
        tab_id: t.entity_id?.split("#")[2] || t.name?.toLowerCase() || `tab_${idx}`,
        name: t.name,
        order: Number(t.display_order ?? idx),
        card_count: t.card_count || 0,
        is_active: t.is_active,
      }));
    },
    refetchOnWindowFocus: false,
  });

  const tabsList = useMemo(() => (tabsQuery.data || []).sort((a, b) => a.order - b.order), [tabsQuery.data]);

  const deleteTabName = useMemo(() => {
    if (!deleteTabId) return "";
    const tab = tabsList.find((t) => t.tab_id === deleteTabId);
    return tab?.name || "";
  }, [deleteTabId, tabsList]);
  useEffect(() => {
    if (!tabsList.length) { setSelectedCardsTabId(null); return; }
    // If no selected tab, default to first
    if (!selectedCardsTabId) setSelectedCardsTabId(tabsList[0].tab_id);
    // If selected tab no longer exists, reset to first
    else if (!tabsList.find(t => t.tab_id === selectedCardsTabId)) setSelectedCardsTabId(tabsList[0].tab_id);
  }, [tabsList, selectedCardsTabId]);

  const tabsSignature = useMemo(
    () => tabsList.map((t) => `${t.tab_id}:${t.order}:${t.card_count}`).join("|"),
    [tabsList]
  );

  const allCardsQuery = useQuery({
    queryKey: [
      "event-markets",
      "cards",
      "aggregate",
      entityType,
      activeSportId || "none",
      tabsSignature,
    ],
    enabled: !!activeSportId,
    queryFn: async () => {
      if (!tabsList.length) return [];
      const tabOrderMap = new Map();
      tabsList.forEach((tab, index) => {
        tabOrderMap.set(tab.tab_id, typeof tab.order === "number" ? tab.order : index + 1);
      });

      const responses = await Promise.all(
        tabsList.map(async (tab, tabIndex) => {
          const res = await api.post(API_ROUTES.GET_MARKET_ENTITIES_BY_TYPE, {
            entity_type: entityType,
            sport_id: activeSportId,
            item_type: "CARD",
            parent_type: "TAB",
            parent_id: tab.tab_id,
          });
          const items = res?.data?.data?.entities_data || [];
          return items.map((c, idx) => ({
            entity_id: c.entity_id,
            market_id: c.market_id,
            name: c.name,
            display_name: c.display_name || c.name,
            order: Number(c.display_order ?? idx + 1),
            is_active: c.is_active,
            tab_id: tab.tab_id,
            tab_name: tab.name,
            tab_order: tabOrderMap.get(tab.tab_id) ?? tabIndex + 1,
          }));
        })
      );

      return responses
        .flat()
        .sort((a, b) => {
          if (a.tab_order === b.tab_order) return a.order - b.order;
          return a.tab_order - b.tab_order;
        });
    },
    refetchOnWindowFocus: false,
  });

  // Reorder tabs
  const reorderTabsMutation = useMutation({
    mutationFn: async (orderedTabIds) => {
      const res = await api.post(API_ROUTES.REORDER_MARKET_ENTITIES, {
        entity_type: entityType,
        sport_id: activeSportId,
        parent_type: "SPORT",
        item_type: "TAB",
        parent_id: activeSportId,
        ordered_ids: orderedTabIds,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-markets", "tabs", entityType, activeSportId] });
      message.success("Tab order updated");
    },
    onError: () => message.error("Failed to update tab order"),
  });

  // Reorder cards within tab
  const reorderCardsMutation = useMutation({
    mutationFn: async ({ tabId, orderedMarketIds }) => {
      const res = await api.post(API_ROUTES.REORDER_MARKET_ENTITIES, {
        entity_type: entityType,
        sport_id: activeSportId,
        parent_type: "TAB",
        parent_id: tabId,
        item_type: "CARD",
        ordered_ids: orderedMarketIds,
      });
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["event-markets", "cards"] });
      message.success("Card order updated");
    },
    onError: () => message.error("Failed to update card order"),
  });

  // Create tab
  const createTabMutation = useMutation({
    mutationFn: async (tabName) => {
      const res = await api.post(API_ROUTES.CREATE_NEW_MARKET_TAB, {
        entity_type: entityType,
        sport_id: activeSportId,
        tab_name: tabName,
      });
      return res.data;
    },
    onSuccess: () => {
      setShowAddTabModal(false);
      setNewTabName("");
      qc.invalidateQueries({ queryKey: ["event-markets", "tabs", entityType, activeSportId] });
      message.success("Tab created");
    },
    onError: () => message.error("Failed to create tab"),
  });

  // Delete tab
  const deleteTabMutation = useMutation({
    mutationFn: async (tabId) => {
      const res = await api.post(API_ROUTES.DELETE_MARKET_TAB, {
        entity_type: entityType,
        sport_id: activeSportId,
        tab_id: tabId,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-markets", "tabs", entityType, activeSportId] });
      message.success("Tab deleted");
      setDeleteTabId(null);
    },
    onError: () => message.error("Failed to delete tab"),
  });

  // Create card
  const createCardMutation = useMutation({
    mutationFn: async ({ tabId, marketId }) => {
      const res = await api.post(API_ROUTES.CREATE_NEW_MARKET_CARD, {
        entity_type: entityType,
        sport_id: activeSportId,
        tab_id: tabId,
        market_id: marketId,
      });
      return res.data;
    },
    onSuccess: (_d, vars) => {
      setShowAddCardModal(false);
      setAddMarketSelected(null);
      setAddMarketQuery("");
      setTargetTabForAddCard(null);
      setDebouncedMarketQuery("");
      qc.invalidateQueries({ queryKey: ["event-markets", "cards"] });
      qc.invalidateQueries({ queryKey: ["event-markets", "tabs", entityType, activeSportId] });
      message.success("Card added to tab");
    },
    onError: () => message.error("Failed to add card"),
  });

  // Delete card
  const deleteCardMutation = useMutation({
    mutationFn: async ({ tabId, marketId }) => {
      const res = await api.post(API_ROUTES.DELETE_MARKET_CARD, {
        entity_type: entityType,
        sport_id: activeSportId,
        tab_id: tabId,
        market_id: marketId,
      });
      return res.data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["event-markets", "cards"] });
      qc.invalidateQueries({ queryKey: ["event-markets", "tabs", entityType, activeSportId] });
      message.success("Card deleted");
      setDeleteCardTarget(null);
    },
    onError: () => message.error("Failed to delete card"),
  });

  // Rename market (display name)
  const renameMarketMutation = useMutation({
    mutationFn: async ({ marketId, newDisplayName }) => {
      const res = await api.post(API_ROUTES.RENAME_MARKET, {
        entity_type: entityType,
        sport_id: activeSportId,
        market_id: marketId,
        new_display_name: newDisplayName,
      });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event-markets", "cards"] });
      qc.invalidateQueries({ queryKey: ["event-markets", "tabs", entityType, activeSportId] });
      setShowRenameMarketModal(false);
      setRenameMarketPayload(null);
      setNewMarketDisplayName("");
      message.success("Market renamed");
    },
    onError: () => message.error("Failed to rename market"),
  });

  // Autocomplete markets with debounced search and cached results
  useEffect(() => {
    if (!showAddCardModal) {
      setDebouncedMarketQuery("");
      return;
    }
    const trimmed = addMarketQuery.trim();
    if (trimmed.length < 2) {
      setDebouncedMarketQuery("");
      return;
    }
    const handler = setTimeout(() => {
      setDebouncedMarketQuery((prev) => (prev === trimmed ? prev : trimmed));
    }, 300);
    return () => clearTimeout(handler);
  }, [addMarketQuery, showAddCardModal, entityType, activeSportId]);

  useEffect(() => {
    if (!showAddCardModal) return;
    setDebouncedMarketQuery("");
  }, [entityType, activeSportId, showAddCardModal]);

  const autocompleteQuery = useQuery({
    queryKey: [
      "event-markets",
      "autocomplete",
      entityType,
      activeSportId || "none",
      debouncedMarketQuery || "",
    ],
    enabled:
      Boolean(api) &&
      Boolean(activeSportId) &&
      Boolean(showAddCardModal) &&
      debouncedMarketQuery.length >= 2,
    queryFn: async () => {
      const res = await api.post(API_ROUTES.GET_SPORT_MARKETS_AUTOCOMPLETE, {
        entity_type: entityType,
        sport_id: activeSportId,
        search_term: debouncedMarketQuery,
        limit: 15,
      });
      const items = res?.data?.data || [];
      return items.map((i) => ({
        entity_id: i.entity_id || `MKT#${activeSportId}#${i.market_id}`,
        market_id: i.market_id,
        item_name: i.display_name || i.name,
        item_type: "MARKET",
        sport_name: undefined,
      }));
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const addMarketOptions = autocompleteQuery.data || [];
  const addMarketLoading = autocompleteQuery.isFetching;
  const addMarketError = autocompleteQuery.isError
    ? autocompleteQuery.error?.response?.data?.message || autocompleteQuery.error?.message || "Failed to search"
    : null;
  const trimmedAddMarketQuery = addMarketQuery.trim();
  const showAutocompleteDropdown = trimmedAddMarketQuery.length >= 2;

  // Sortables refs
  const tabsTbodyRef = useRef(null);
  const tabsSortableRef = useRef(null);
  const cardsSortableRefs = useRef({});

  useEffect(() => {
    setSelectedCardsTabId(null);
  }, [entityType]);

  useEffect(() => {
    setSelectedCardsTabId(null);
  }, [activeSportId]);

  useEffect(() => {
    return () => {
      Object.values(cardsSortableRefs.current).forEach((instance) => {
        try { instance?.destroy(); } catch (_) {}
      });
      cardsSortableRefs.current = {};
    };
  }, []);

  useEffect(() => {
    if (!tabsTbodyRef.current) return;
    if (tabsSortableRef.current) {
      try { tabsSortableRef.current.destroy(); } catch (_) {}
      tabsSortableRef.current = null;
    }
    tabsSortableRef.current = Sortable.create(tabsTbodyRef.current, {
      animation: 150,
      handle: ".drag-handle",
      draggable: ".tab-row",
      onEnd: () => {
        const nodes = Array.from(tabsTbodyRef.current.querySelectorAll(".tab-row"));
        const orderedTabIds = nodes
          .map((n) => n.getAttribute("data-tab-id"))
          .filter(Boolean);
        if (orderedTabIds.length) {
          reorderTabsMutation.mutate(orderedTabIds);
        }
      },
    });
    return () => {
      try { tabsSortableRef.current?.destroy(); } catch (_) {}
      tabsSortableRef.current = null;
    };
  }, [entityType, activeSportId, tabsList.length]);

  const bindCardsSortable = (tabId) => (el) => {
    if (!tabId) return;
    const existing = cardsSortableRefs.current[tabId];

    if (!el) {
      if (existing) {
        try { existing.destroy(); } catch (_) {}
        delete cardsSortableRefs.current[tabId];
      }
      return;
    }

    if (existing) {
      try { existing.destroy(); } catch (_) {}
      delete cardsSortableRefs.current[tabId];
    }

    cardsSortableRefs.current[tabId] = Sortable.create(el, {
      animation: 150,
      handle: ".drag-handle",
      draggable: ".card-row",
      onEnd: () => {
        const nodes = Array.from(el.querySelectorAll(".card-row"));
        const orderedMarketIds = nodes
          .map((n) => n.getAttribute("data-market-id"))
          .filter(Boolean);
        if (orderedMarketIds.length) {
          const currentOrder = (allCardsQuery.data || [])
            .filter((card) => card.tab_id === tabId)
            .map((card) => card.market_id);
          const isSameOrder =
            currentOrder.length === orderedMarketIds.length &&
            currentOrder.every((id, index) => id === orderedMarketIds[index]);
          if (!isSameOrder) {
            reorderCardsMutation.mutate({ tabId, orderedMarketIds });
          }
        }
      },
    });
  };

  const handleOpenAddCard = (tabId) => {
    setTargetTabForAddCard(tabId);
    setSelectedCardsTabId(tabId);
    setShowAddCardModal(true);
    setAddMarketQuery("");
    setAddMarketSelected(null);
    setDebouncedMarketQuery("");
  };

  const renderSportsTabs = () => (
    <>
      <div className="em-sports-header">
        <div className="em-sports-list">
          {(sportsQuery.data || []).map((s) => (
            <div
              key={s.id}
              className={`em-sport-pill ${activeSportId === s.id ? "active" : ""}`}
              onClick={() => setActiveSportId(s.id)}
            >
              <img src={ClipPath} alt="ClipPath" />
              <span>{s.name}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="em-entity-type-toggle-outside">
        <div className="em-entity-type-toggle">
          <button
            className={entityType === ENTITY_TYPES.PREMATCH ? "active" : ""}
            onClick={() => {
              setEntityType(ENTITY_TYPES.PREMATCH);
            }}
          >
            Pre match
          </button>
          <button
            className={entityType === ENTITY_TYPES.LIVE ? "active" : ""}
            onClick={() => {
              setEntityType(ENTITY_TYPES.LIVE);
            }}
          >
            Live
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="event-markets">
      {renderSportsTabs()}

      <div className="em-section-header">
        <h3>Market Tabs</h3>
        <button className="em-primary" onClick={() => setShowAddTabModal(true)}>+ Add Tab</button>
      </div>
      <p className="em-section-subtitle">Manage the tab titles displayed on the frontend for events. Drag to reorder tabs.</p>

      <div className="em-table-wrapper">
        <table className="em-table">
          <thead>
            <tr>
              <th># Order</th>
              <th>Tab name</th>
              <th>Markets</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody ref={tabsTbodyRef}>
            {tabsList.length === 0 ? (
              <tr className="em-empty-row">
                <td colSpan={4} className="em-empty">
                  No market tabs found for this {entityTypeLabel.toLowerCase()} {activeSportName}
                </td>
              </tr>
            ) : tabsList.map((tab, idx) => {
              const tabId = tab.tab_id;
              return (
                <tr
                  key={tab.entity_id}
                  className={`tab-row${selectedCardsTabId === tabId ? " selected" : ""}`}
                  data-tab-id={tabId}
                  onClick={() => setSelectedCardsTabId(tabId)}
                >
                  <td className="order-cell">
                    <div className="order-cell-inner">
                      <img className="drag-handle" src={ClipPath} alt="drag" />
                      <div className="order-pill">{idx + 1}</div>
                    </div>
                  </td>
                  <td>{tab.name}</td>
                  <td>{tab.card_count}</td>
                  <td className="actions">
                    <button className="em-icon-btn" title="Add market to tab" onClick={() => handleOpenAddCard(tabId)}>
                      <Plus size={14} />
                    </button>
                    <button className="em-icon-btn danger" title="Delete tab" onClick={() => setDeleteTabId(tabId)}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="em-section-header em-tab-cards-intro">
        <div>
          <h3>Markets/Game Cards</h3>
          <p>Manage market/card titles and their placement within tabs. Each market/card has a UOF ID for backend communication.</p>
        </div>
        <span />
      </div>

      {tabsList.map((tab) => {
        const tabCards = (allCardsQuery.data || []).filter((card) => card.tab_id === tab.tab_id);
        const tabId = tab.tab_id;
        return (
          <div key={`${tab.entity_id}-cards`} className="em-tab-cards-section">
            <div className="em-section-header">
              <h4 className="em-tab-name">{tab.name}</h4>
              <button className="em-primary" onClick={() => handleOpenAddCard(tabId)}>Add Market/Game card</button>
            </div>
            <div className="em-table-wrapper">
              <table className="em-subtable-table em-cards-table">
                <thead>
                  <tr>
                    <th># Order</th>
                    <th>ID</th>
                    <th>Display market name</th>
                    <th>Original name</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody ref={bindCardsSortable(tabId)}>
                  <CardsTableRows
                    cards={tabCards}
                    isLoading={allCardsQuery.isLoading || allCardsQuery.isFetching}
                    onRename={(card) => {
                      if (card.tab_id) setSelectedCardsTabId(card.tab_id);
                      setRenameMarketPayload({ sport_id: activeSportId, market_id: card.market_id, current_display_name: card.display_name });
                      setNewMarketDisplayName(card.display_name || card.name || "");
                      setShowRenameMarketModal(true);
                    }}
                    onDelete={(card) => {
                      if (card.tab_id) setSelectedCardsTabId(card.tab_id);
                      setDeleteCardTarget({ tabId: card.tab_id, marketId: card.market_id });
                    }}
                  />
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <Modal closable={false} footer={null} width={600} open={showAddTabModal} onCancel={() => setShowAddTabModal(false)}>
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title"><h2 className="modal-title">Add Market Tab</h2></div>
            <X onClick={() => setShowAddTabModal(false)} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <label htmlFor="tabName">Tab Name</label>
            <Input id="tabName" placeholder="Enter tab name" value={newTabName} onChange={(e) => setNewTabName(e.target.value)} />
          </div>
          <div className="modal-footer">
            <button className="cancel-button" onClick={() => setShowAddTabModal(false)} disabled={createTabMutation.isPending}>Close</button>
            <button className={`submit-button ${!newTabName.trim() || createTabMutation.isPending ? 'disabled' : ''}`} disabled={!newTabName.trim() || createTabMutation.isPending} onClick={() => createTabMutation.mutate(newTabName.trim())}>
              {createTabMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        closable={false}
        footer={null}
        width={600}
        open={showAddCardModal}
        onCancel={() => {
          setShowAddCardModal(false);
          setTargetTabForAddCard(null);
          setAddMarketQuery("");
          setAddMarketSelected(null);
          setDebouncedMarketQuery("");
        }}
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title"><h2 className="modal-title">Add Market/Game Card</h2></div>
            <X onClick={() => setShowAddCardModal(false)} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <div className="autocomplete">
              <Input id="marketSearch" placeholder="Start typing to search for a market or game card..." value={addMarketQuery} onChange={(e) => setAddMarketQuery(e.target.value)} />
              {showAutocompleteDropdown ? (
                <div className="results">
                  {addMarketLoading ? (
                    <div className="center"><Spin size="small" /></div>
                  ) : (
                    addMarketOptions.map((opt) => (
                      <div
                        key={opt.entity_id}
                        className={`result-item ${addMarketSelected?.entity_id === opt.entity_id ? 'selected' : ''}`}
                        onClick={() => setAddMarketSelected(opt)}
                      >
                        <div className="result-main">
                          <span className="result-name">{opt.item_name}</span>
                          <Tag size="small" color={'gold'}>MARKET</Tag>
                        </div>
                      </div>
                    ))
                  )}
                  {!addMarketLoading && addMarketOptions.length === 0 && debouncedMarketQuery.length >= 2 && (
                    <div className="empty-mini">No results</div>
                  )}
                </div>
              ) : null}
            </div>
            {addMarketError && debouncedMarketQuery.length >= 2 ? (
              <div className="error-msg">{addMarketError}</div>
            ) : null}
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={() => {
                setShowAddCardModal(false);
                setTargetTabForAddCard(null);
                setAddMarketQuery("");
                setAddMarketSelected(null);
                setDebouncedMarketQuery("");
              }}
              disabled={createCardMutation.isPending}
            >
              Close
            </button>
            <button className={`submit-button ${!addMarketSelected || createCardMutation.isPending ? 'disabled' : ''}`} disabled={!addMarketSelected || createCardMutation.isPending} onClick={() => targetTabForAddCard && addMarketSelected && createCardMutation.mutate({ tabId: targetTabForAddCard, marketId: addMarketSelected.market_id })}>
              {createCardMutation.isPending ? 'Adding...' : 'Add'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal closable={false} footer={null} width={500} open={showRenameMarketModal} onCancel={() => setShowRenameMarketModal(false)}>
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title"><h2 className="modal-title">Edit Market Name</h2></div>
            <X onClick={() => setShowRenameMarketModal(false)} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <label htmlFor="marketDisplayName">Display Name</label>
            <Input id="marketDisplayName" value={newMarketDisplayName} onChange={(e) => setNewMarketDisplayName(e.target.value)} />
          </div>
          <div className="modal-footer">
            <button className="cancel-button" onClick={() => setShowRenameMarketModal(false)} disabled={renameMarketMutation.isPending}>Close</button>
            <button className={`submit-button ${!newMarketDisplayName.trim() || renameMarketMutation.isPending ? 'disabled' : ''}`} disabled={!newMarketDisplayName.trim() || renameMarketMutation.isPending} onClick={() => renameMarketPayload?.market_id && renameMarketMutation.mutate({ marketId: renameMarketPayload.market_id, newDisplayName: newMarketDisplayName.trim() })}>
              {renameMarketMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal closable={false} footer={null} width={500} open={!!deleteTabId} onCancel={() => setDeleteTabId(null)}>
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title"><h2 className="modal-title">Delete Market Tab</h2></div>
            <X onClick={() => setDeleteTabId(null)} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <p>
              {`Are you sure you want to delete market tab "${deleteTabName || "this tab"}"?`}
            </p>
          </div>
          <div className="modal-footer">
            <button className="cancel-button" onClick={() => setDeleteTabId(null)} disabled={deleteTabMutation.isPending}>Close</button>
            <button className={`submit-button delete-button ${deleteTabMutation.isPending ? 'disabled' : ''}`} onClick={() => deleteTabMutation.mutate(deleteTabId)} disabled={deleteTabMutation.isPending}>
              {deleteTabMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal closable={false} footer={null} width={500} open={!!deleteCardTarget} onCancel={() => setDeleteCardTarget(null)}>
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title"><h2 className="modal-title">Delete Market</h2></div>
            <X onClick={() => setDeleteCardTarget(null)} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <p>Are you sure you want to delete this market from the tab?</p>
          </div>
          <div className="modal-footer">
            <button className="cancel-button" onClick={() => setDeleteCardTarget(null)} disabled={deleteCardMutation.isPending}>Close</button>
            <button className={`submit-button delete-button ${deleteCardMutation.isPending ? 'disabled' : ''}`} onClick={() => deleteCardTarget && deleteCardMutation.mutate(deleteCardTarget)} disabled={deleteCardMutation.isPending}>
              {deleteCardMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function CardsTableRows({ cards, isLoading, onRename, onDelete }) {
  if (isLoading) {
    return (
      <tr className="em-empty-row">
        <td colSpan={5} className="em-empty">Loading markets...</td>
      </tr>
    );
  }

  if (!cards || cards.length === 0) {
    return (
      <tr className="em-empty-row">
        <td colSpan={5} className="em-empty">There are no markets for this market tab</td>
      </tr>
    );
  }

  return (
    <>
      {cards.map((card, idx) => {
        const displayOrder = typeof card.order === "number" ? card.order : idx + 1;
        return (
          <tr
            className="card-row"
            key={card.entity_id}
            data-id={card.entity_id}
            data-market-id={card.market_id}
            data-tab-id={card.tab_id || ""}
            title={card.tab_name ? `Tab: ${card.tab_name}` : undefined}
          >
            <td className="order-cell">
              <div className="order-cell-inner">
                <img className="drag-handle" src={ClipPath} alt="drag" />
                <div className="order-pill">{displayOrder}</div>
              </div>
            </td>
            <td>{card.market_id}</td>
            <td>{card.display_name}</td>
            <td>{card.name}</td>
            <td className="actions">
              <button className="em-icon-btn" title="Edit market name" onClick={() => onRename(card)}>
                <Edit2 size={14} />
              </button>
              <button className="em-icon-btn danger" title="Delete market" onClick={() => onDelete(card)}>
                <Trash2 size={14} />
              </button>
            </td>
          </tr>
        );
      })}
    </>
  );
}

export default EventMarkets;

